import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaUser, FaGamepad, FaEye, FaClock } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext.js';
import { getServices } from '../firebase.js';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';

const Following = () => {
  const { currentUser } = useAuth();
  const [followedStreamers, setFollowedStreamers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { db } = getServices();
  
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }
    
    const fetchFollowedStreamers = async () => {
      try {
        setLoading(true);
        
        // Get user's following list
        const userFollowingRef = collection(db, 'users', currentUser.uid, 'following');
        const followingSnapshot = await getDocs(userFollowingRef);
        
        if (followingSnapshot.empty) {
          setFollowedStreamers([]);
          setLoading(false);
          return;
        }
        
        // Extract IDs of followed users
        const followedUserIds = followingSnapshot.docs.map(doc => doc.id);
        
        // Get details of all followed streamers
        const streamerDetails = [];
        
        // Get live streams for the followed users
        const liveStreamsQuery = query(
          collection(db, 'streams'),
          where('userId', 'in', followedUserIds),
          where('status', '==', 'live')
        );
        
        const liveStreamsSnapshot = await getDocs(liveStreamsQuery);
        const liveStreamsByUser = {};
        
        // Create a map of live streams by user ID
        liveStreamsSnapshot.forEach(streamDoc => {
          const streamData = streamDoc.data();
          liveStreamsByUser[streamData.userId] = {
            id: streamDoc.id,
            ...streamData
          };
        });
        
        // Fetch user details for each followed user
        for (const userId of followedUserIds) {
          try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            
            if (userDoc.exists()) {
              const userData = userDoc.data();
              const isLive = Boolean(liveStreamsByUser[userId]);
              
              streamerDetails.push({
                id: userId,
                username: userData.username || userData.displayName?.toLowerCase() || 'user',
                displayName: userData.displayName || userData.username || 'Unknown User',
                isLive: isLive,
                avatar: userData.photoURL || `https://api.dicebear.com/7.x/avataaars/svg?seed=${userId}`,
                
                // If live, add stream details
                ...(isLive && {
                  game: liveStreamsByUser[userId].game || 'Unknown Game',
                  title: liveStreamsByUser[userId].title || 'Live Stream',
                  viewerCount: liveStreamsByUser[userId].viewerCount || 0,
                  startedAt: liveStreamsByUser[userId].startedAt?.toDate() || new Date()
                }),
                
                // If not live, add last stream time if available
                ...(!isLive && {
                  lastStream: userData.lastStreamDate?.toDate() || new Date(Date.now() - 86400000)
                })
              });
            }
          } catch (error) {
            console.error(`Error fetching user ${userId}:`, error);
          }
        }
        
        setFollowedStreamers(streamerDetails);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching followed streamers:', error);
        setLoading(false);
      }
    };
    
    fetchFollowedStreamers();
  }, [currentUser, db]);
  
  // Format time duration (e.g., "2h 15m")
  const formatStreamDuration = (startTime) => {
    const durationMs = Date.now() - startTime;
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };
  
  // Format date (e.g., "2 days ago")
  const formatLastStream = (date) => {
    const diff = Math.floor((Date.now() - date) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    return `${diff} days ago`;
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col space-y-8">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <motion.h1 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl font-bold text-white"
          >
            Following
          </motion.h1>
        </div>
        
        {loading ? (
          // Loading state
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(5)].map((_, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-harmony-blue-dark rounded-xl overflow-hidden animate-pulse"
              >
                <div className="h-40 bg-harmony-blue/50" />
                <div className="p-4 space-y-2">
                  <div className="h-6 bg-harmony-blue/50 rounded w-2/3" />
                  <div className="h-4 bg-harmony-blue/50 rounded w-1/2" />
                  <div className="h-4 bg-harmony-blue/50 rounded w-1/3" />
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <>
            {/* Live Streams Section */}
            {followedStreamers.some(streamer => streamer.isLive) && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">Live Now</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {followedStreamers
                    .filter(streamer => streamer.isLive)
                    .map((streamer, index) => (
                      <motion.div
                        key={streamer.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 * index }}
                        className="bg-harmony-blue-dark rounded-xl overflow-hidden border border-harmony-white/10 hover:border-harmony-orange/30 transition-all"
                      >
                        <Link to={`/stream/${streamer.username}`} className="block">
                          <div className="relative">
                            <div className="w-full h-40 bg-harmony-blue/50" />
                            <div className="absolute top-2 left-2">
                              <span className="bg-red-600 text-white text-xs px-2 py-1 rounded uppercase tracking-wider">
                                Live
                              </span>
                            </div>
                            <div className="absolute bottom-2 right-2">
                              <span className="bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center">
                                <FaEye className="mr-1" />
                                {streamer.viewerCount.toLocaleString()}
                              </span>
                            </div>
                          </div>
                          
                          <div className="p-4">
                            <div className="flex items-start">
                              <img 
                                src={streamer.avatar} 
                                alt={streamer.displayName} 
                                className="w-10 h-10 rounded-full mr-3"
                              />
                              <div>
                                <h3 className="font-semibold text-white">{streamer.displayName}</h3>
                                <p className="text-sm text-harmony-white/70 truncate">{streamer.title}</p>
                                <div className="flex items-center mt-2 space-x-4">
                                  <span className="text-xs text-harmony-white/60 flex items-center">
                                    <FaGamepad className="mr-1" />
                                    {streamer.game}
                                  </span>
                                  <span className="text-xs text-harmony-white/60 flex items-center">
                                    <FaClock className="mr-1" />
                                    {formatStreamDuration(streamer.startedAt)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                </div>
              </div>
            )}
            
            {/* Offline Streamers Section */}
            {followedStreamers.some(streamer => !streamer.isLive) && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">Offline Channels</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {followedStreamers
                    .filter(streamer => !streamer.isLive)
                    .map((streamer, index) => (
                      <motion.div
                        key={streamer.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 * index }}
                        className="bg-harmony-blue-dark rounded-lg p-4 border border-harmony-white/5 hover:border-harmony-white/20 transition-all"
                      >
                        <Link to={`/user/${streamer.username}`} className="flex items-center">
                          <img 
                            src={streamer.avatar} 
                            alt={streamer.displayName} 
                            className="w-12 h-12 rounded-full mr-3"
                          />
                          <div>
                            <h3 className="font-medium text-white">{streamer.displayName}</h3>
                            <div className="flex items-center mt-1">
                              <span className="text-xs text-harmony-white/60">
                                Last live: {formatLastStream(streamer.lastStream)}
                              </span>
                            </div>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                </div>
              </div>
            )}
            
            {/* Empty State */}
            {followedStreamers.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="bg-harmony-blue-dark rounded-3xl p-8 max-w-md">
                  <FaUser className="text-5xl text-harmony-white/30 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">You're not following anyone yet</h3>
                  <p className="text-harmony-white/70 mb-6">
                    Follow streamers to see them here when they go live.
                  </p>
                  <Link 
                    to="/browse" 
                    className="px-6 py-2 bg-harmony-orange text-white rounded-lg hover:bg-harmony-orange/90 transition-colors inline-block"
                  >
                    Discover Streamers
                  </Link>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Following; 